import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../engine/api/context';
import { ApiError } from '../../engine/api/client';
import type { TileDef } from '../../engine/module';
import { Button, IconButton } from '../../engine/design/primitives/Button';
import { Checkbox } from '../../engine/design/primitives/Input';
import { Drawer } from '../../engine/design/primitives/Overlay';
import { useToast } from '../../engine/design/primitives/Toast';
import type { BandDef } from './tiles';

// A user's Home layout — tile order and hidden tiles — is their default `home` saved view
// (platform primitive, /v1/saved-views): `columns` is the tile order, `query.hidden` the hidden
// keys. A tenant admin may share a default; a personal one wins. No view = declared order.

export interface HomeLayout {
  order: string[];
  hidden: string[];
}

interface HomeView {
  id: string;
  resource: string;
  owner_user_id: string | null;
  is_default: boolean;
  columns: unknown;
  query: { hidden?: unknown } | null;
}

const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
const EMPTY: HomeLayout = { order: [], hidden: [] };

/** Tiles of one band in the user's order (unknown keys after, in declared order); hidden ones dropped unless asked. */
export function arrange(tiles: TileDef[], band: string, layout: HomeLayout, includeHidden = false): TileDef[] {
  const pos = (k: string) => {
    const i = layout.order.indexOf(k);
    return i === -1 ? Number.MAX_SAFE_INTEGER : i;
  };
  return tiles
    .map((t, i) => ({ t, i }))
    .filter(({ t }) => t.band === band && (includeHidden || !layout.hidden.includes(t.key)))
    .sort((a, b) => pos(a.t.key) - pos(b.t.key) || a.i - b.i)
    .map(({ t }) => t);
}

export function useHomeLayout() {
  const api = useApi();
  const qc = useQueryClient();
  const toast = useToast();
  const key = ['saved-views', 'home'];
  const views = useQuery<HomeView[]>({
    queryKey: key,
    staleTime: 60_000,
    queryFn: async () => {
      try {
        return (await api.resource<HomeView>('saved-views').list({ filter: [{ field: 'resource', op: 'eq', value: 'home' }], limit: 20 })).data;
      } catch (e) {
        if (e instanceof ApiError && (e.isNotFound || e.isNetwork || e.status === 501)) return [];
        throw e;
      }
    },
  });
  const rows = (views.data ?? []).filter((v) => v.resource === 'home');
  const personal = rows.find((v) => v.owner_user_id !== null && v.is_default) ?? null;
  const shared = rows.find((v) => v.owner_user_id === null && v.is_default) ?? null;
  const active = personal ?? shared;
  const layout = useMemo<HomeLayout>(() => (active ? { order: strings(active.columns), hidden: strings(active.query?.hidden) } : EMPTY), [active]);

  const save = useMutation({
    mutationFn: (l: HomeLayout) => {
      const body = { columns: l.order, query: { hidden: l.hidden } };
      return personal ? api.resource('saved-views').update(personal.id, body) : api.resource('saved-views').create({ resource: 'home', name: 'My home', is_default: true, ...body });
    },
    onSuccess: () => {
      toast({ title: 'Home layout saved' });
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => toast({ kind: 'error', title: 'Could not save the layout', detail: e instanceof ApiError ? e.detail : String(e) }),
  });
  const reset = useMutation({
    mutationFn: () => (personal ? api.resource('saved-views').remove(personal.id) : Promise.resolve()),
    onSuccess: () => {
      toast({ title: shared ? "Home reset to your team's layout" : 'Home reset to the default layout' });
      void qc.invalidateQueries({ queryKey: key });
    },
    onError: (e) => toast({ kind: 'error', title: 'Could not reset the layout', detail: e instanceof ApiError ? e.detail : String(e) }),
  });
  return { layout, personal: personal !== null, save, reset };
}

/** "Customise": show/hide each tile and move it within its band (keyboard: the ↑/↓ buttons). */
export function CustomiseHome({ open, onClose, tiles, bands, layout, onSave, onReset, canReset, busy }: { open: boolean; onClose: () => void; tiles: TileDef[]; bands: BandDef[]; layout: HomeLayout; onSave: (l: HomeLayout) => void; onReset: () => void; canReset: boolean; busy?: boolean }) {
  const [draft, setDraft] = useState<HomeLayout>(layout);
  useEffect(() => {
    if (open) setDraft(layout);
  }, [open, layout]);

  const move = (band: string, key: string, by: -1 | 1) => {
    const ordered = bands.flatMap((b) => arrange(tiles, b.key, draft, true).map((t) => t.key));
    const inBand = arrange(tiles, band, draft, true).map((t) => t.key);
    const i = inBand.indexOf(key);
    const j = i + by;
    if (j < 0 || j >= inBand.length) return;
    const swapped = [...inBand];
    [swapped[i], swapped[j]] = [swapped[j]!, swapped[i]!];
    let k = 0;
    setDraft({ ...draft, order: ordered.map((x) => (inBand.includes(x) ? swapped[k++]! : x)) });
  };
  const toggle = (key: string, shown: boolean) => setDraft({ ...draft, hidden: shown ? draft.hidden.filter((h) => h !== key) : [...draft.hidden, key] });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Customise Home"
      description="Choose which tiles you see and their order. Only you see this layout."
      footer={
        <>
          {canReset && <Button variant="ghost" onClick={onReset} busy={busy}>Reset to default</Button>}
          <Button variant="primary" onClick={() => onSave(draft)} busy={busy}>Save layout</Button>
        </>
      }
    >
      <div className="hf-stack">
        {bands.map((b) => {
          const list = arrange(tiles, b.key, draft, true);
          if (list.length === 0) return null;
          return (
            <section key={b.key} aria-label={b.label} className="hf-stack" style={{ gap: 'var(--s-1)' }}>
              <div className="hf-caps">{b.label}</div>
              {list.map((t, i) => (
                <div key={t.key} className="hf-between hf-customise-row">
                  <Checkbox label={t.label} checked={!draft.hidden.includes(t.key)} onChange={(e) => toggle(t.key, e.target.checked)} />
                  <span className="hf-row" style={{ gap: 0 }}>
                    <IconButton size="sm" icon="arrow-up" label={`Move ${t.label} up`} disabled={i === 0} onClick={() => move(b.key, t.key, -1)} />
                    <IconButton size="sm" icon="arrow-down" label={`Move ${t.label} down`} disabled={i === list.length - 1} onClick={() => move(b.key, t.key, 1)} />
                  </span>
                </div>
              ))}
            </section>
          );
        })}
      </div>
    </Drawer>
  );
}
